<!-- Native input with Element search-bar classes, because `si-search-bar` offers no way to bind combobox ARIA attributes. -->
<div class="search-bar h-9 w-full min-w-0 rounded-full">
    <si-icon class="search-bar-icon inset-s-3!" [icon]="icons.elementSearch" />
    <input
        #searchInput
        class="search-bar-input form-control h-full! min-h-0! rounded-full! ps-11! bg-[color-mix(in_srgb,var(--element-base-1)_55%,transparent)]! hover:bg-[color-mix(in_srgb,var(--element-base-1)_88%,transparent)]! focus:bg-[color-mix(in_srgb,var(--element-base-1)_88%,transparent)]!"
        type="text"
        placeholder="Themen und Nuggets durchsuchen..."
        aria-label="Themen und Nuggets durchsuchen"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        [class.icon-end]="inputValue()"
        [value]="inputValue()"
        [attr.aria-expanded]="isListboxRendered()"
        [attr.aria-controls]="isListboxRendered() ? listboxId : null"
        [attr.aria-activedescendant]="activeDescendant()"
        (input)="onInput($event)"
        (focusin)="open()"
        (keydown)="onKeydown($event)"
    />
    @if (inputValue()) {
        <button
            class="btn btn-icon btn-sm btn-tertiary-ghost search-bar-clear"
            type="button"
            aria-label="Suche löschen"
            (click)="clearInput()"
        >
            <si-icon [icon]="icons.elementCancel" />
        </button>
    }
</div>

<span class="sr-only" aria-live="polite">
    @if (hasSearchableTerm() && !isLoading()) {
        {{ resultCount() }} Suchergebnisse
    }
</span>

@if (isOpen()) {
    <div
        class="absolute inset-x-0 top-[calc(100%+0.75rem)] z-100 flex flex-col overflow-hidden rounded-2xl border border-white/12 bg-[rgba(28,28,48,0.97)] text-white shadow-[0_24px_48px_rgba(0,0,0,0.45)] backdrop-blur-[13px]"
    >
        <div class="min-h-0 max-h-[min(60vh,32rem)] overflow-y-auto">
            @if (isLoading()) {
                <div class="flex items-center gap-3 px-5 py-8 text-sm text-white/70">
                    <si-loading-spinner ariaLabel="Suche läuft..." />
                    <span>Suche läuft...</span>
                </div>
            } @else if (hasError()) {
                <si-empty-state
                    class="grid place-content-center px-5 py-8 text-center"
                    icon="element-warning-filled"
                    heading="Suche nicht möglich"
                    content="Die Suche ist fehlgeschlagen. Bitte versuchen Sie es erneut."
                />
            } @else if (!hasSearchableTerm()) {
                @if (recentTerms().length > 0) {
                    <div class="px-5 py-4">
                        <div class="mb-3 flex items-center justify-between gap-4">
                            <span class="text-xs font-semibold tracking-wide text-white/50 uppercase">
                                Zuletzt gesucht
                            </span>
                            <button
                                class="text-xs text-white/60 underline-offset-2 hover:text-white hover:underline"
                                type="button"
                                (click)="forgetRecentTerms()"
                            >
                                Verlauf löschen
                            </button>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            @for (recentTerm of recentTerms(); track recentTerm) {
                                <button
                                    class="flex items-center gap-2 rounded-full border border-white/12 bg-white/6 px-3 py-1.5 text-sm text-white/85 hover:border-[#00e5aa]/60 hover:bg-white/12 hover:text-white"
                                    type="button"
                                    (click)="applyRecentTerm(recentTerm)"
                                >
                                    <si-icon class="text-base text-white/50" [icon]="icons.elementClock" />
                                    <span>{{ recentTerm }}</span>
                                </button>
                            }
                        </div>
                    </div>
                } @else {
                    <p class="px-5 py-8 text-center text-sm text-white/60">
                        Mindestens {{ minTermLength }} Zeichen eingeben, um zu suchen.
                    </p>
                }
            } @else if (groups().length === 0) {
                <si-empty-state
                    class="grid place-content-center px-5 py-8 text-center"
                    icon="element-search"
                    heading="Keine Treffer für „{{ term() }}“"
                    content="Prüfen Sie die Schreibweise oder suchen Sie nach einem anderen Begriff."
                />
            } @else {
                <ul class="list-none py-2" role="listbox" [id]="listboxId" aria-label="Suchergebnisse">
                    @for (group of groups(); track group.key) {
                        <li role="presentation">
                            <div
                                class="sticky top-0 z-10 flex items-center gap-2 bg-[rgba(28,28,48,0.97)] px-5 py-2 text-xs font-semibold tracking-wide text-white/50 uppercase"
                            >
                                <si-icon class="text-base" [icon]="group.icon" />
                                <span>{{ group.label }}</span>
                                <span class="text-white/35">({{ group.results.length }})</span>
                            </div>
                            <ul class="list-none" role="presentation">
                                @for (result of group.results; track result.id) {
                                    <li role="presentation">
                                        <button
                                            class="flex w-full items-center gap-3 px-5 py-2.5 text-left text-sm text-white/85 hover:bg-white/8"
                                            type="button"
                                            role="option"
                                            tabindex="-1"
                                            [id]="optionId(result.index)"
                                            [class]="
                                                activeIndex() === result.index
                                                    ? 'bg-white/12 text-white shadow-[inset_3px_0_0_#00e5aa]'
                                                    : ''
                                            "
                                            [attr.aria-selected]="activeIndex() === result.index"
                                            (mouseenter)="activeIndex.set(result.index)"
                                            (click)="selectResult(result)"
                                        >
                                            <span class="min-w-0 flex-1">
                                                <span class="block truncate">
                                                    @for (part of result.titleParts; track $index) {
                                                        <span
                                                            [class]="part.match ? 'font-semibold text-[#00e5aa]' : ''"
                                                        >
                                                            {{ part.text }}
                                                        </span>
                                                    }
                                                </span>
                                                @if (result.subtitle) {
                                                    <span class="block truncate text-xs text-white/50">
                                                        {{ result.subtitle }}
                                                    </span>
                                                }
                                            </span>
                                        </button>
                                    </li>
                                }
                            </ul>
                        </li>
                    }
                </ul>
            }
        </div>

        <div
            class="flex items-center gap-4 border-t border-white/10 px-5 py-2 text-[11px] text-white/45"
            aria-hidden="true"
        >
            <!-- Rendered as spans because the theme paints <kbd> as a solid white keycap. -->
            <span class="flex items-center gap-1">
                <span class="rounded-sm border border-white/15 bg-white/8 px-1.5 py-0.5 leading-none">↑</span>
                <span class="rounded-sm border border-white/15 bg-white/8 px-1.5 py-0.5 leading-none">↓</span>
                Navigieren
            </span>
            <span class="flex items-center gap-1">
                <span class="rounded-sm border border-white/15 bg-white/8 px-1.5 py-0.5 leading-none">↵</span>
                Öffnen
            </span>
            <span class="flex items-center gap-1">
                <span class="rounded-sm border border-white/15 bg-white/8 px-1.5 py-0.5 leading-none">Esc</span>
                Schließen
            </span>
        </div>
    </div>
}
